// @ts-check
// "Atualizar registros" (doc 10, seção 15): escolhe um ou mais campos, define o valor e aplica em todos os selecionados.
// Campo escolhido e deixado vazio limpa o valor; campo não escolhido não muda. Cada registro é aplicado por conta
// própria no motor, então os recusados (ex.: perda sem motivo) aparecem no resumo sem desfazer os outros.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { updateManyRecords } from '../../data/parity.js';
import { FIELD_TYPE, getFieldIcon, getRelationIdFieldName, isFieldReadOnly, isManyToOneRelation } from '../../lib/field-metadata.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { Icon } from '../../ui/icon.js';
import { Button, IconButton, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSearchInput, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { Modal } from '../../ui/modal.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { FormField, toApiValue } from './create-record-panel.js';
import { closeSidePanel } from './side-panel-state.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/** @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata */

// tipos que o formulário de criação já sabe editar, menos os de contato (um mesmo e-mail em vários registros não faz sentido)
const BULK_FIELD_TYPES = new Set(['TEXT', 'NUMBER', 'CURRENCY', 'DATE', 'DATE_TIME', 'SELECT', 'MULTI_SELECT', 'BOOLEAN', 'RATING', 'RELATION']);

/**
 * Campos que a atualização em lote oferece: editáveis, não únicos, relações só do lado "muitos para um".
 * @param {import('../../lib/field-metadata.js').ObjectMetadata} objectMetadata
 * @returns {FieldMetadata[]}
 */
export function getBulkEditableFields(objectMetadata) {
  return objectMetadata.fields
    .filter((field) => BULK_FIELD_TYPES.has(field.type) && field.isActive !== false && !field.isSystem && !field.isUnique && !isFieldReadOnly(field)
      && (field.type !== FIELD_TYPE.RELATION || isManyToOneRelation(field)))
    .sort((first, second) => translateMetadataLabel(first.label).localeCompare(translateMetadataLabel(second.label)));
}

/**
 * Corpo do lote: valores vazios viram null (limpar), relações viram <campo>Id.
 * @param {FieldMetadata[]} fields @param {Record<string, any>} values
 */
export function buildBulkPatch(fields, values) {
  /** @type {Record<string, unknown>} */
  const patch = {};
  for (const field of fields) {
    const value = values[field.name];
    if (isManyToOneRelation(field)) {
      patch[getRelationIdFieldName(field)] = value?.id ?? null;
      continue;
    }
    if (field.type === FIELD_TYPE.BOOLEAN) {
      patch[field.name] = Boolean(value);
      continue;
    }
    if (field.type === FIELD_TYPE.MULTI_SELECT) {
      patch[field.name] = Array.isArray(value) ? value : [];
      continue;
    }
    const apiValue = toApiValue(field, value);
    patch[field.name] = apiValue === undefined ? null : apiValue;
  }
  return patch;
}

/**
 * @param {{ objectNameSingular: string, ids: string[], presetFieldName?: string | null, onDone?: () => void }} props
 */
export function UpdateRecordsPanel({ objectNameSingular, ids, presetFieldName = null, onDone }) {
  const objectMetadata = objectsByNameSingular.value.get(objectNameSingular);
  const addAnchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [chosenNames, setChosenNames] = useState(/** @type {string[]} */ (presetFieldName ? [presetFieldName] : []));
  const [values, setValues] = useState(/** @type {Record<string, any>} */ ({}));
  const [isConfirming, setIsConfirming] = useState(false);
  const [progress, setProgress] = useState(/** @type {number | null} */ (null));
  if (!objectMetadata) return null;
  const editableFields = getBulkEditableFields(objectMetadata);
  const chosenFields = chosenNames.map((name) => editableFields.find((field) => field.name === name)).filter(Boolean);
  const availableFields = editableFields.filter((field) => !chosenNames.includes(field.name)
    && normalizeSearchText(translateMetadataLabel(field.label)).includes(normalizeSearchText(searchText)));

  const apply = async () => {
    setIsConfirming(false);
    setProgress(0);
    try {
      const result = await updateManyRecords(objectMetadata, ids, buildBulkPatch(/** @type {FieldMetadata[]} */ (chosenFields), values), setProgress);
      if (result.errors.length > 0) {
        enqueueSnackbar({
          message: t('parity.bulk.updatedWithErrors', { count: result.records.length, failed: result.errors.length }),
          detailedMessage: result.errors[0].message,
          variant: 'warning',
        });
      } else {
        showSuccessSnackbar(t('parity.bulk.updated', { count: result.records.length }));
      }
      onDone?.();
      closeSidePanel();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setProgress(null);
    }
  };

  return html`<form class="side-panel-page" onSubmit=${(/** @type {Event} */ event) => {
    event.preventDefault();
    if (chosenFields.length > 0) setIsConfirming(true);
  }}>
    <div class="side-panel-top-bar">
      <div class="side-panel-top-bar-left">
        <span class="side-panel-page-chip">
          <${Icon} name="IconEdit" size=${16} />
          <span>${t('parity.bulk.updateTitle', { count: ids.length })}</span>
        </span>
      </div>
      <${IconButton} icon="IconX" size="small" variant="tertiary" ariaLabel=${t('sidePanel.close')} onClick=${closeSidePanel} />
    </div>
    <div class="side-panel-content create-record-form bulk-update-form">
      <p class="create-record-hint">${t('parity.bulk.updateHint')}</p>
      ${chosenFields.map((field) => html`<div class="bulk-update-row" key=${field.id}>
        <${FormField} field=${field} value=${values[field.name]} onChange=${(value) => setValues((current) => ({ ...current, [field.name]: value }))} />
        <${LightIconButton} icon="IconX" ariaLabel=${t('parity.bulk.removeField')} onClick=${() => setChosenNames(chosenNames.filter((name) => name !== field.name))} />
      </div>`)}
      <button ref=${addAnchorRef} type="button" class="light-button bulk-update-add" onClick=${() => setIsAddOpen(!isAddOpen)}>
        <${Icon} name="IconPlus" size=${14} />
        <span>${t('parity.bulk.addField')}</span>
      </button>
      <${Dropdown} anchorRef=${addAnchorRef} isOpen=${isAddOpen} onClose=${() => setIsAddOpen(false)} width=${260}>
        <${DropdownMenuSearchInput} value=${searchText} onChange=${setSearchText} placeholder=${t('viewBar.searchFields')} />
        <${DropdownMenuSeparator} />
        <${DropdownMenuItems} hasMaxHeight=${true}>
          ${availableFields.length === 0 ? html`<div class="menu-item-empty">${t('common.noResults')}</div>` : null}
          ${availableFields.map((field) => html`<${MenuItem} key=${field.id} icon=${getFieldIcon(field)} text=${translateMetadataLabel(field.label)} onClick=${() => {
            setChosenNames([...chosenNames, field.name]);
            setIsAddOpen(false);
            setSearchText('');
          }} />`)}
        <//>
      <//>
    </div>
    <div class="side-panel-footer">
      ${progress !== null ? html`<span class="bulk-update-progress">${t('parity.bulk.progress', { done: progress, total: ids.length })}</span>` : null}
      <${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${closeSidePanel} />
      <${Button} title=${t('parity.bulk.apply')} icon="IconBoxMultiple" accent="blue" size="small" type="submit" disabled=${chosenFields.length === 0} isLoading=${progress !== null} />
    </div>
    ${isConfirming
      ? html`<${Modal}
          title=${t('parity.bulk.confirmUpdateTitle', { count: ids.length })}
          subtitle=${t('parity.bulk.confirmUpdateSubtitle', { count: ids.length })}
          confirmLabel=${t('parity.bulk.confirmUpdate')}
          onClose=${() => setIsConfirming(false)}
          onConfirm=${apply}
        />`
      : null}
  </form>`;
}
